export interface StepDefinition {
  key: string;
  titleKey: string;
  descriptionKey: string;
  navigationTarget: string;
  /** CSS selector pulsed on the destination page after overlay navigation. */
  spotlight?: string;
  requiredRoles?: string[];
  requiredPlugins?: string[];
  excludedCustomerTypes?: string[];
}

export interface TrackDefinition {
  key: string;
  titleKey: string;
  requiredRoles?: string[];
  steps: StepDefinition[];
}

export interface ActiveStep {
  key: string;
  titleKey: string;
  descriptionKey: string;
  navigationTarget: string;
  spotlight?: string;
  /** Plugins the step's target page needs — used to avoid navigating into a
   *  locked-door page when the CURRENT event doesn't have them enabled. */
  requiredPlugins?: string[];
  trackKey: string;
  isCompleted: boolean;
  completedAt: string | null;
}

export const ONBOARDING_TRACKS: TrackDefinition[] = [
  {
    key: "COMPANY_SETUP",
    titleKey: "tracks.COMPANY_SETUP.title",
    requiredRoles: ["OWNER"],
    steps: [
      {
        key: "complete_company_profile",
        titleKey: "steps.complete_company_profile.title",
        descriptionKey: "steps.complete_company_profile.description",
        navigationTarget: "/user/company",
        excludedCustomerTypes: ["INDIVIDUAL"],
      },
      {
        key: "choose_plan",
        spotlight: '[data-testid="onboarding-change-plan"]',
        titleKey: "steps.choose_plan.title",
        descriptionKey: "steps.choose_plan.description",
        navigationTarget: "/user/subscription",
      },
    ],
  },
  {
    key: "FIRST_EVENT",
    titleKey: "tracks.FIRST_EVENT.title",
    requiredRoles: ["OWNER", "ADMIN"],
    steps: [
      {
        key: "create_event",
        spotlight: '[data-tour-anchor="tour-create-event"]',
        titleKey: "steps.create_event.title",
        descriptionKey: "steps.create_event.description",
        navigationTarget: "/create-event",
      },
      {
        key: "add_first_guest",
        spotlight: '[data-testid="event-guestlist-create-guest-btn"]',
        titleKey: "steps.add_first_guest.title",
        descriptionKey: "steps.add_first_guest.description",
        navigationTarget: "/event/{eventId}/guests/guestlist",
      },
      {
        key: "configure_event_settings",
        titleKey: "steps.configure_event_settings.title",
        descriptionKey: "steps.configure_event_settings.description",
        navigationTarget: "/event/{eventId}/settings/general",
      },
    ],
  },
  {
    key: "EVENT_ESSENTIALS",
    titleKey: "tracks.EVENT_ESSENTIALS.title",
    requiredRoles: ["OWNER", "ADMIN", "EDITOR"],
    steps: [
      {
        key: "explore_guestlist",
        titleKey: "steps.explore_guestlist.title",
        descriptionKey: "steps.explore_guestlist.description",
        navigationTarget: "/event/{eventId}/guests/guestlist",
      },
      {
        key: "setup_website",
        titleKey: "steps.setup_website.title",
        descriptionKey: "steps.setup_website.description",
        navigationTarget: "/website-builder/{eventId}/configurator",
        requiredPlugins: ["WEBSITE"],
      },
      {
        key: "create_email_template",
        spotlight: '[data-testid="onboarding-create-template"]',
        titleKey: "steps.create_email_template.title",
        descriptionKey: "steps.create_email_template.description",
        navigationTarget: "/event/{eventId}/messaging/mail-templates",
        requiredPlugins: ["MESSAGING"],
      },
      {
        key: "setup_sender_domain",
        spotlight: '[data-testid="onboarding-create-sender"]',
        titleKey: "steps.setup_sender_domain.title",
        descriptionKey: "steps.setup_sender_domain.description",
        navigationTarget: "/event/{eventId}/messaging/sender",
        requiredPlugins: ["MESSAGING"],
        requiredRoles: ["OWNER", "ADMIN"],
      },
      {
        key: "explore_check_in",
        titleKey: "steps.explore_check_in.title",
        descriptionKey: "steps.explore_check_in.description",
        navigationTarget: "/event/{eventId}/check-in/history",
        requiredPlugins: ["CHECK_IN"],
      },
      {
        key: "explore_seating",
        titleKey: "steps.explore_seating.title",
        descriptionKey: "steps.explore_seating.description",
        navigationTarget: "/event/{eventId}/seating",
        requiredPlugins: ["SEATING"],
      },
      {
        key: "explore_actions",
        titleKey: "steps.explore_actions.title",
        descriptionKey: "steps.explore_actions.description",
        navigationTarget: "/event/{eventId}/automation/workflows",
        requiredPlugins: ["ACTIONS"],
      },
      {
        key: "explore_attachments",
        titleKey: "steps.explore_attachments.title",
        descriptionKey: "steps.explore_attachments.description",
        navigationTarget: "/event/{eventId}/attachments",
        requiredPlugins: ["ATTACHMENTS"],
      },
      {
        key: "explore_documents",
        titleKey: "steps.explore_documents.title",
        descriptionKey: "steps.explore_documents.description",
        navigationTarget: "/event/{eventId}/documents",
        requiredPlugins: ["DOCUMENTS"],
      },
    ],
  },
  {
    key: "ADVANCED_FEATURES",
    titleKey: "tracks.ADVANCED_FEATURES.title",
    requiredRoles: ["OWNER", "ADMIN", "EDITOR"],
    steps: [
      {
        key: "invite_collaborator",
        spotlight: '[data-testid="onboarding-add-collaborator"]',
        titleKey: "steps.invite_collaborator.title",
        descriptionKey: "steps.invite_collaborator.description",
        navigationTarget: "/user/collaborators",
        requiredRoles: ["OWNER", "ADMIN"],
        excludedCustomerTypes: ["INDIVIDUAL"],
      },
      {
        key: "import_guests",
        spotlight: '[data-testid="guests-actions"]',
        titleKey: "steps.import_guests.title",
        descriptionKey: "steps.import_guests.description",
        navigationTarget: "/event/{eventId}/guests/guestlist",
      },
      {
        key: "explore_templates",
        titleKey: "steps.explore_templates.title",
        descriptionKey: "steps.explore_templates.description",
        navigationTarget: "/templates",
        requiredRoles: ["OWNER", "ADMIN"],
      },
      {
        key: "explore_dashboard",
        titleKey: "steps.explore_dashboard.title",
        descriptionKey: "steps.explore_dashboard.description",
        navigationTarget: "/event/{eventId}/dashboard",
        requiredPlugins: ["DASHBOARD"],
      },
      {
        key: "configure_plugins",
        spotlight: '[data-onboarding-target="plugins-grid"]',
        titleKey: "steps.configure_plugins.title",
        descriptionKey: "steps.configure_plugins.description",
        navigationTarget: "/event/{eventId}/settings/plugins",
        requiredRoles: ["OWNER", "ADMIN"],
      },
      // Programm-Zeitstrahl merge: slotting's front door is the programme
      // timeline — the slotting steps navigate there DIRECTLY (the old
      // /slotting target only redirected). Step keys are wire format
      // (backend progress state) and stay untouched; only copy + targets
      // moved to bookable-block language. The spotlight testId now sits on
      // the page's ONE generic add CTA (the separate bookable-block button
      // was folded into it) — the step copy tells the user to pick the
      // bookable kind inside the form.
      {
        key: "create_first_offering",
        spotlight: '[data-testid="onboarding-create-offering"]',
        titleKey: "steps.create_first_offering.title",
        descriptionKey: "steps.create_first_offering.description",
        navigationTarget: "/event/{eventId}/programme?view=timeline",
        requiredPlugins: ["SLOTTING", "PROGRAM"],
      },
      {
        key: "create_first_lane",
        titleKey: "steps.create_first_lane.title",
        descriptionKey: "steps.create_first_lane.description",
        navigationTarget: "/event/{eventId}/programme?view=timeline",
        requiredPlugins: ["SLOTTING", "PROGRAM"],
      },
      {
        key: "create_first_treatment",
        titleKey: "steps.create_first_treatment.title",
        descriptionKey: "steps.create_first_treatment.description",
        navigationTarget: "/event/{eventId}/programme?view=timeline",
        requiredPlugins: ["SLOTTING", "PROGRAM"],
      },
      {
        key: "create_first_appointment",
        titleKey: "steps.create_first_appointment.title",
        descriptionKey: "steps.create_first_appointment.description",
        navigationTarget: "/event/{eventId}/programme?view=timeline",
        requiredPlugins: ["SLOTTING", "PROGRAM"],
      },
      {
        key: "discover_quick_search",
        spotlight: '[data-tour-anchor="tour-global-search"]',
        titleKey: "steps.discover_quick_search.title",
        descriptionKey: "steps.discover_quick_search.description",
        navigationTarget: "/event/{eventId}/dashboard",
      },
      {
        key: "send_first_email",
        spotlight: '[data-testid="onboarding-send-emails"]',
        titleKey: "steps.send_first_email.title",
        descriptionKey: "steps.send_first_email.description",
        navigationTarget: "/event/{eventId}/messaging/mail-templates",
        requiredPlugins: ["MESSAGING"],
      },
    ],
  },
  {
    key: "VIEWER_INTRO",
    titleKey: "tracks.VIEWER_INTRO.title",
    requiredRoles: ["VIEWER"],
    steps: [
      {
        key: "view_event_dashboard",
        titleKey: "steps.view_event_dashboard.title",
        descriptionKey: "steps.view_event_dashboard.description",
        navigationTarget: "/event/{eventId}/dashboard",
        requiredPlugins: ["DASHBOARD"],
      },
      {
        key: "browse_guestlist",
        titleKey: "steps.browse_guestlist.title",
        descriptionKey: "steps.browse_guestlist.description",
        navigationTarget: "/event/{eventId}/guests/guestlist",
      },
    ],
  },
];

export function getActiveSteps(
  tracks: TrackDefinition[],
  // Defensive: progress docs from older backends can lack activeTracks
  // entirely — iterating undefined crashed the whole coach (UX-polish A,
  // item 12).
  activeTracks: string[] | undefined,
  companyRole: string,
  plugins: string[],
  completedSteps: Record<string, string>,
  customerType?: string | null,
): ActiveStep[] {
  const role = companyRole.toUpperCase();
  const pluginSet = new Set(plugins.map((p) => p.toUpperCase()));
  const custType = customerType?.toUpperCase() ?? null;

  const result: ActiveStep[] = [];

  for (const trackKey of activeTracks ?? []) {
    const track = tracks.find((t) => t.key === trackKey);
    if (!track) continue;

    if (track.requiredRoles && !track.requiredRoles.includes(role)) continue;

    const trackSteps: ActiveStep[] = [];

    for (const step of track.steps) {
      if (step.requiredRoles && !step.requiredRoles.includes(role)) continue;
      if (
        step.requiredPlugins &&
        !step.requiredPlugins.every((p) => pluginSet.has(p.toUpperCase()))
      )
        continue;
      if (
        custType &&
        step.excludedCustomerTypes?.some((t) => t.toUpperCase() === custType)
      )
        continue;

      const completedAt = completedSteps?.[step.key] ?? null;

      trackSteps.push({
        key: step.key,
        titleKey: step.titleKey,
        descriptionKey: step.descriptionKey,
        navigationTarget: step.navigationTarget,
        spotlight: step.spotlight,
        requiredPlugins: step.requiredPlugins,
        trackKey: track.key,
        isCompleted: completedAt !== null,
        completedAt,
      });
    }

    // Completed steps first within each track group, preserving relative order
    const completed = trackSteps.filter((s) => s.isCompleted);
    const incomplete = trackSteps.filter((s) => !s.isCompleted);
    result.push(...completed, ...incomplete);
  }

  return result;
}
